import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { FieldArrayWithId, UseFieldArrayAppend } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';

import type { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { DataTable, numberColumn, textColumn } from 'components/DataTable';
import { SectionContainer } from 'components/DetailsView/Section';
import { Loader, LoaderModeType } from 'components/Loader';

import { formatEuroAmount } from 'helpers/price';

import { useGetSupplyOrderProductsToAddQuery } from 'data/__generated__';

import {
    getProductFormValueFromCatalogue,
    SupplyOrderDetails,
    SupplyOrderFormValues,
    SupplyOrderProductToAdd,
} from '../supplyOrderFormValues';
import { StockVariationPreviewCell, useSupplyOrderStockVariations } from '../StockVariationPreviewCell';

type Props = {
    supplyOrder: SupplyOrderDetails;
    productFields: FieldArrayWithId<SupplyOrderFormValues, 'products', 'id'>[];
    onAddProduct: UseFieldArrayAppend<SupplyOrderFormValues, 'products'>;
};

/**
 * Le catalogue du fournisseur MOINS ce qui est déjà sur l'achat — le filtrage est fait par l'API.
 *
 * Le tableau principal ne montre que ce qui est commandé : c'est ici qu'on ajoute une référence, et
 * elle n'existe côté serveur qu'à l'enregistrement, seulement si on lui a donné des colis. La section
 * « Prévisions » sert la même requête pour nommer les références non commandées.
 */
export const ProductsToAddSection = (props: Props) => (
    <SectionContainer title="Produits du fournisseur non commandés" lazy>
        <ProductsToAddContent {...props} />
    </SectionContainer>
);

const ProductsToAddContent = ({ supplyOrder, productFields, onAddProduct }: Props) => {
    const { data, loading, error } = useGetSupplyOrderProductsToAddQuery({
        variables: { supplyOrderId: supplyOrder._id },
    });

    const stockVariations = useSupplyOrderStockVariations(supplyOrder._id);

    const alreadyOnOrderIds = useMemo(() => new Set(productFields.map(({ _id }) => _id)), [productFields]);

    const columns = useMemo<ColumnDef<SupplyOrderProductToAdd, unknown>[]>(
        () => [
            textColumn<SupplyOrderProductToAdd, Record<string, unknown>>({
                id: 'product',
                accessorFn: (row) => `${row.name} - ${row.brand} - ${row.volume}`,
                header: 'Produit',
                defaultVisible: true,
                truncate: false,
                cell: ({ row }) => (
                    <div className="flex min-w-0 items-center gap-2">
                        <Link className="truncate hover:underline" to={`/product/${row.original._id}`}>
                            {`${row.original.name} - ${row.original.brand} - ${row.original.volume}`}
                        </Link>
                        {row.original.isToBeArchived ? <Badge variant="outline">À sortir</Badge> : null}
                        {row.original.supplierShortageInfo.inShortage ? (
                            <Badge variant="destructive">En rupture</Badge>
                        ) : null}
                    </div>
                ),
            }),
            numberColumn<SupplyOrderProductToAdd, Record<string, unknown>>({
                accessorKey: 'stock',
                header: 'Stock',
                defaultVisible: true,
            }),
            numberColumn<SupplyOrderProductToAdd, Record<string, unknown>>({
                accessorKey: 'availableStock',
                header: 'Stock dispo',
                defaultVisible: true,
            }),
            numberColumn<SupplyOrderProductToAdd, Record<string, unknown>>({
                accessorKey: 'conditionning',
                header: 'Colisage',
                defaultVisible: true,
            }),
            numberColumn<SupplyOrderProductToAdd, Record<string, unknown>>({
                accessorKey: 'unitPrice',
                header: 'PU HT',
                defaultVisible: true,
                cell: ({ getValue }) => <span>{formatEuroAmount(getValue() as number)}</span>,
            }),
            {
                id: 'stockVariation',
                header: 'Variation de stock',
                enableSorting: false,
                meta: { defaultVisible: true },
                cell: ({ row }) => (
                    <StockVariationPreviewCell
                        stockVariations={stockVariations}
                        productId={row.original._id}
                        productInfo={row.original}
                        stock={row.original.stock}
                        orderingTimes={supplyOrder.supplier.orderingTimes}
                        shouldDisplayTooltip={row.index < 3}
                    />
                ),
            },
            {
                id: '_add',
                header: '',
                meta: { defaultVisible: true },
                cell: ({ row }) => {
                    const isAdded = alreadyOnOrderIds.has(row.original._id);
                    return (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isAdded}
                            data-test="add-product-to-supply-order"
                            onClick={() => {
                                onAddProduct(getProductFormValueFromCatalogue(row.original));
                                toast.info(
                                    `${row.original.name} ajouté à l'achat — indiquez un nombre de colis puis enregistrez.`,
                                );
                            }}
                        >
                            <Plus />
                            {isAdded ? 'Ajouté' : 'Ajouter'}
                        </Button>
                    );
                },
            },
        ],
        [alreadyOnOrderIds, onAddProduct, stockVariations, supplyOrder.supplier.orderingTimes],
    );

    if (loading) {
        return (
            <div className="flex h-40 w-full items-center justify-center">
                <Loader mode={LoaderModeType.Spin} />
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-destructive">Erreur lors du chargement du catalogue fournisseur.</p>;
    }

    const products = data?.supplyOrderProductsToAdd ?? [];

    return (
        <div className="flex max-h-[70vh] w-full flex-col">
            {/* Virtualisée : un fournisseur peut porter plusieurs centaines de références, chacune avec son
                aperçu de variations jour par jour. */}
            <DataTable<SupplyOrderProductToAdd>
                enableVirtualization
                estimatedRowHeight={110}
                columns={columns}
                data={products}
                filterMode="client"
                disableUrlSync
                autoFocusSearch={false}
                getRowId={(row) => row._id}
                searchPlaceholder="Rechercher une référence du fournisseur…"
                emptyMessage="Toutes les références de ce fournisseur sont déjà sur l'achat."
            />
        </div>
    );
};
